import { useSearchParams } from 'react-router';
import type { AppUser, MeResponse } from '@contracts/auth';
import { Banner, Panel } from '../components/Panel';
import { useLogout } from '../hooks/useMe';

const ERRORS: Record<string, string> = {
  cancelled: 'การเข้าสู่ระบบถูกยกเลิก',
  login_expired: 'ลิงก์เข้าสู่ระบบหมดอายุหรือถูกใช้ไปแล้ว กรุณาลองใหม่',
  login_not_configured: 'เซิร์ฟเวอร์ยังไม่ได้ตั้งค่า Login with LINE',
  too_many_logins: 'มีการเข้าสู่ระบบพร้อมกันมากเกินไป กรุณาลองใหม่อีกครั้ง',
};

/** Sign-in page. The button is a plain link: the backend redirects to LINE and back. */
export function LoginPage({ me }: { me?: MeResponse }) {
  const [params] = useSearchParams();
  const error = params.get('error');
  return (
    <div className="auth-page">
      <Panel title="เข้าสู่ระบบ">
        <p>MagicTradie! แจ้งเตือนสัญญาณ XAUUSD เฉพาะผู้ใช้ที่ได้รับอนุมัติ</p>
        {error ? (
          <Banner tone="warn" title="เข้าสู่ระบบไม่สำเร็จ">
            {ERRORS[error] ?? `LINE login failed (${error}). กรุณาลองใหม่`}
          </Banner>
        ) : null}
        {me && !me.loginConfigured ? (
          <Banner tone="info" title="Login with LINE is not configured on this server" />
        ) : (
          <a className="btn btn--line" href="/api/v1/auth/line/login">
            <span aria-hidden="true">💬</span> เข้าสู่ระบบด้วย LINE
          </a>
        )}
        <p className="small muted">
          ระบบจะชวนให้เพิ่ม LINE Official Account เป็นเพื่อน เพื่อให้ส่งแจ้งเตือนถึงคุณได้
          ผู้ใช้ใหม่ต้องรอแอดมินอนุมัติก่อน
        </p>
      </Panel>
    </div>
  );
}

/** Shown to signed-in users who are not approved (pending or disabled). */
export function PendingPage({ user }: { user: AppUser }) {
  const logout = useLogout();
  const disabled = user.status === 'disabled';
  return (
    <div className="auth-page">
      <Panel title={disabled ? 'บัญชีถูกปิดใช้งาน' : 'รอการอนุมัติ'}>
        <div className="row">
          {user.pictureUrl ? <img src={user.pictureUrl} alt="" width={48} height={48} className="avatar" /> : null}
          <div>
            <strong>{user.displayName || 'LINE user'}</strong>
            <div className="small muted">
              LINE user ID: <code>{user.lineUserId}</code>
            </div>
          </div>
        </div>
        <p>
          {disabled
            ? 'บัญชีนี้ถูกปิดใช้งาน ติดต่อแอดมินหากคิดว่าผิดพลาด'
            : 'แอดมินจะอนุมัติบัญชีของคุณ หลังอนุมัติแล้วรีเฟรชหน้านี้เพื่อเข้าใช้งาน'}
        </p>
        {user.isFriend === false ? (
          <Banner tone="warn" title="ยังไม่ได้เพิ่ม LINE Official Account เป็นเพื่อน">
            เพิ่มเป็นเพื่อนก่อน ไม่อย่างนั้นจะไม่ได้รับแจ้งเตือน (ออกจากระบบแล้วเข้าใหม่เพื่ออัปเดตสถานะ)
          </Banner>
        ) : null}
        <div className="row">
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            รีเฟรช
          </button>
          <button type="button" className="btn" onClick={() => logout.mutate()} disabled={logout.isPending}>
            ออกจากระบบ
          </button>
        </div>
      </Panel>
    </div>
  );
}
